// frontend/src/lib/api/chatCreation.service.ts
import { apiClient } from './client';
import type {
  CelebrationDraftDto,
  ChatActivitySuggestionDto,
  ChatEventSuggestionDto,
  ChatGoalSuggestionDto,
  ChatServiceSuggestionDto,
} from '@om/shared-types';

const GUEST_PREFIX = 'guest-';
const GUEST_CHAT_TOKEN_STORAGE_KEY = 'om_guest_chat_token';

const generateGuestChatToken = (): string => {
  const runtimeCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (runtimeCrypto?.randomUUID) {
    return `${GUEST_PREFIX}${runtimeCrypto.randomUUID()}`;
  }

  return `${GUEST_PREFIX}${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
};

const getOrCreateGuestChatToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const existing = window.localStorage.getItem(GUEST_CHAT_TOKEN_STORAGE_KEY);
    if (existing && existing.startsWith(GUEST_PREFIX)) {
      return existing;
    }

    const token = generateGuestChatToken();
    window.localStorage.setItem(GUEST_CHAT_TOKEN_STORAGE_KEY, token);
    return token;
  } catch {
    return null;
  }
};

export const getGuestChatHeaders = (): Record<string, string> => {
  const guestToken = getOrCreateGuestChatToken();
  if (!guestToken) {
    return {};
  }

  return {
    'X-Guest-Token': guestToken,
  };
};

const toOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const toOptionalNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

const toOptionalStringArray = (value: unknown): string[] | undefined => {
  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => String(item).trim())
      .filter(Boolean);

    return normalized.length > 0 ? normalized : undefined;
  }

  return undefined;
};

export const normalizeCelebrationDraft = (value: unknown): CelebrationDraftDto => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const source = value as Record<string, unknown>;
  const draft: CelebrationDraftDto = {
    title: toOptionalString(source.title),
    celebration_type: toOptionalString(source.celebration_type),
    celebration_subtype: toOptionalString(source.celebration_subtype),
    template: toOptionalString(source.template),
    description: toOptionalString(source.description),
    start_date: toOptionalString(source.start_date),
    end_date: toOptionalString(source.end_date),
    budget: toOptionalNumber(source.budget),
    location_name: toOptionalString(source.location_name),
    latitude: toOptionalNumber(source.latitude),
    longitude: toOptionalNumber(source.longitude),
    radius_miles: toOptionalNumber(source.radius_miles),
    rsvp_deadline: toOptionalString(source.rsvp_deadline),
    culture: toOptionalString(source.culture),
    religion: toOptionalString(source.religion),
    style: toOptionalString(source.style),
    guest_count_estimate: toOptionalNumber(source.guest_count_estimate),
    celebrant_names: toOptionalStringArray(source.celebrant_names),
    celebrant_roles: toOptionalStringArray(source.celebrant_roles),
    celebrant_emails: toOptionalStringArray(source.celebrant_emails),
    _celebrant_role: toOptionalString(source._celebrant_role),
    _celebrant_email: toOptionalString(source._celebrant_email),
  };

  return Object.fromEntries(
    Object.entries(draft).filter(([, entryValue]) => entryValue !== undefined),
  ) as CelebrationDraftDto;
};

const isChatEventSuggestion = (value: unknown): value is ChatSuggestedEvent => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return typeof candidate.id === 'string'
    && typeof candidate.title === 'string'
    && typeof candidate.eventType === 'string';
};

const isChatActivitySuggestion = (value: unknown): value is ChatSuggestedActivity => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return typeof candidate.id === 'string'
    && typeof candidate.title === 'string'
    && typeof candidate.eventId === 'string';
};

const isChatGoalSuggestion = (value: unknown): value is ChatSuggestedGoal => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return typeof candidate.id === 'string'
    && typeof candidate.title === 'string'
    && typeof candidate.eventId === 'string';
};

const isChatServiceSuggestion = (value: unknown): value is ChatSuggestedService => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return typeof candidate.serviceDefinitionId === 'number'
    && typeof candidate.category === 'string'
    && typeof candidate.name === 'string';
};

const normalizeEventSuggestions = (value: unknown): ChatSuggestedEvent[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const normalized = value.filter(isChatEventSuggestion);
  return normalized;
};

const normalizeActivitySuggestions = (value: unknown): Record<string, ChatSuggestedActivity[]> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const entries = Object.entries(value as Record<string, unknown>).map(([eventId, activities]) => {
    if (!Array.isArray(activities)) {
      return [eventId, []] as const;
    }

    return [eventId, activities.filter(isChatActivitySuggestion)] as const;
  });

  return Object.fromEntries(entries);
};

const normalizeGoalSuggestions = (value: unknown): Record<string, ChatSuggestedGoal[]> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const entries = Object.entries(value as Record<string, unknown>).map(([eventId, goals]) => {
    if (!Array.isArray(goals)) {
      return [eventId, []] as const;
    }

    return [eventId, goals.filter(isChatGoalSuggestion)] as const;
  });

  return Object.fromEntries(entries);
};

const normalizeServiceSuggestions = (value: unknown): ChatSuggestedService[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  return value.filter(isChatServiceSuggestion);
};

const normalizeSelectedServiceDefinitionIds = (value: unknown): number[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const normalized = value
    .map((entry) => (typeof entry === 'number' ? entry : Number(entry)))
    .filter((entry) => Number.isFinite(entry));

  return normalized;
};

export type ChatSuggestedEvent = ChatEventSuggestionDto;

export type ChatSuggestedActivity = ChatActivitySuggestionDto;

export type ChatSuggestedGoal = ChatGoalSuggestionDto;

export type ChatSuggestedService = ChatServiceSuggestionDto;

export type ChatAnalyticsEventType =
  | 'chat_session_started'
  | 'chat_step_changed'
  | 'chat_message_sent'
  | 'chat_selection_made'
  | 'chat_skip_used'
  | 'chat_create_clicked'
  | 'chat_interrupted'
  | 'chat_abandoned';

/**
 * Response shape from the chat creation workflow backend.
 */
export interface ChatCreationResponse {
  success: boolean;
  response: string[];
  sessionId: string;
  conversationId: string;
  status: string;
  activityReviewCurrentEventIndex?: number;
  extractedFields?: CelebrationDraftDto;
  missingFields?: string[];
  templateSuggestions?: Array<{
    key: string;
    name: string;
    description: string;
    matchScore: number;
  }>;
  actionResult?: {
    id: number;
    title: string;
    slug?: string;
    celebration_type: string;
    celebration_subtype?: string;
    template?: string;
    start_date?: string;
    end_date?: string;
    eventsCreated?: number;
    activitiesCreated?: number;
    tasksCreated?: number;
    servicesShortlisted?: number;
  };
  generatedTags?: Record<string, string[]>;
  confirmedTags?: Record<string, string[]>;
  eventSuggestions?: ChatSuggestedEvent[];
  selectedEvents?: ChatSuggestedEvent[];
  activitySuggestions?: Record<string, ChatSuggestedActivity[]>;
  goalSuggestions?: Record<string, ChatSuggestedGoal[]>;
  selectedActivities?: Record<string, string[]>;
  selectedGoalTemplateIdsByEvent?: Record<string, number[]>;
  serviceSuggestions?: ChatSuggestedService[];
  selectedServiceDefinitionIds?: number[];
  addServicesToShortlist?: boolean;
  conversationLogId?: number;
}

export interface ChatTemplateContext {
  sessionId: string;
  conversationId?: string;
  extractedFields?: CelebrationDraftDto;
  confirmedTags?: Record<string, string[]>;
  templateSuggestions?: Array<{
    key: string;
    name: string;
    description: string;
    matchScore: number;
  }>;
}

/**
 * Summary data embedded in extractedFields._summaryData by ConfirmationNode.
 */
export interface CelebrationSummaryData {
  title: string;
  type: string;
  subtype?: string;
  celebrants: string[];
  startDate?: string;
  endDate?: string;
  location?: string;
  culture?: string;
  religion?: string;
  style?: string;
  budget?: number;
  guestCount?: number;
  description?: string;
}

/**
 * Send a message to the chat creation workflow.
 */
export async function sendChatCreationMessage(
  message: string,
  sessionId?: string,
  conversationId?: string
): Promise<ChatCreationResponse> {
  const response = await apiClient.post<ChatCreationResponse>(
    '/chat',
    {
      message,
      sessionId,
      conversationId,
    },
    {
      headers: getGuestChatHeaders(),
    }
  );
  const data = response.data;
  return {
    ...data,
    extractedFields: normalizeCelebrationDraft(data.extractedFields),
    eventSuggestions: normalizeEventSuggestions(data.eventSuggestions),
    selectedEvents: normalizeEventSuggestions(data.selectedEvents),
    activitySuggestions: normalizeActivitySuggestions(data.activitySuggestions),
    goalSuggestions: normalizeGoalSuggestions(data.goalSuggestions),
    serviceSuggestions: normalizeServiceSuggestions(data.serviceSuggestions),
    selectedServiceDefinitionIds: normalizeSelectedServiceDefinitionIds(data.selectedServiceDefinitionIds),
    activityReviewCurrentEventIndex: typeof data.activityReviewCurrentEventIndex === 'number' && Number.isFinite(data.activityReviewCurrentEventIndex)
      ? data.activityReviewCurrentEventIndex
      : undefined,
  };
}

export interface ChatSessionData {
  sessionId: string;
  status: string;
  activityReviewCurrentEventIndex?: number;
  extractedFields: CelebrationDraftDto;
  missingFields: string[];
  confirmedTags?: Record<string, string[]>;
  generatedTags?: Record<string, string[]>;
  eventSuggestions?: ChatSuggestedEvent[];
  selectedEvents?: ChatSuggestedEvent[];
  activitySuggestions?: Record<string, ChatSuggestedActivity[]>;
  goalSuggestions?: Record<string, ChatSuggestedGoal[]>;
  selectedActivities?: Record<string, string[]>;
  selectedGoalTemplateIdsByEvent?: Record<string, number[]>;
  serviceSuggestions?: ChatSuggestedService[];
  selectedServiceDefinitionIds?: number[];
  addServicesToShortlist?: boolean;
  conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>;
  createdAt: string;
  updatedAt: string;
}

/**
 * Fetch an active in-progress chat session from the backend (Redis).
 * Returns null if the session has expired or doesn't exist.
 */
export async function getChatSession(sessionId: string): Promise<ChatSessionData | null> {
  try {
    const response = await apiClient.get<{ success: boolean; data: ChatSessionData }>(
      `/chat/session/${encodeURIComponent(sessionId)}`,
      {
        headers: getGuestChatHeaders(),
      }
    );
    const session = response.data?.data;
    if (!session) {
      return null;
    }

    return {
      ...session,
      extractedFields: normalizeCelebrationDraft(session.extractedFields),
      eventSuggestions: normalizeEventSuggestions(session.eventSuggestions),
      selectedEvents: normalizeEventSuggestions(session.selectedEvents),
      activitySuggestions: normalizeActivitySuggestions(session.activitySuggestions),
      goalSuggestions: normalizeGoalSuggestions(session.goalSuggestions),
      serviceSuggestions: normalizeServiceSuggestions(session.serviceSuggestions),
      selectedServiceDefinitionIds: normalizeSelectedServiceDefinitionIds(session.selectedServiceDefinitionIds),
      activityReviewCurrentEventIndex: typeof session.activityReviewCurrentEventIndex === 'number' && Number.isFinite(session.activityReviewCurrentEventIndex)
        ? session.activityReviewCurrentEventIndex
        : undefined,
    };
  } catch (error) {
    console.warn('[getChatSession] Failed to fetch session from backend:', sessionId, error);
    return null;
  }
}

export async function trackChatAnalyticsEvent(payload: {
  sessionId: string;
  eventType: ChatAnalyticsEventType;
  workflowStatus?: string;
  step?: string;
  reason?: string;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  try {
    await apiClient.post('/chat/analytics/event', payload, {
      headers: getGuestChatHeaders(),
    });
  } catch {
    // Best effort only; analytics failures should never block UX
  }
}
